import { FC, useEffect, useState } from 'react';
import Table from '@components/table';
import { TTransactionLite } from '@xeppt/xeppt-sdk/types/transaction';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiTransactionsService } from '@api';
import { ENewNotificationType } from '@enum';
import styles from './styles.module.scss';
import {
    ETransactionAction,
    ETransactionStatus,
    TCashbackFilter,
    TTransaction
} from '@xeppt/xeppt-sdk/types';
import Status from '@components/common/status';
import { formatDate, getTransactionStatus } from '@utils/index';
import { ETransactionStatusTranslate } from '@locales/index';
import { useTranslation } from 'react-i18next';
import illustration from '@svg/illustrations/empty_withdraw.svg';

interface IProps {
    date_from?: Date;
    date_to?: Date;
}

const WithdrawnTable: FC<IProps> = ({ date_to, date_from }) => {
    const { i18n } = useTranslation('cashback');
    const [cursor, setCursor] = useState<Date>();
    const [filter, setFilter] = useState<TCashbackFilter>({
        order: 'DESC',
        status: undefined
    });
    const [transactions, setTransactions] = useState<TTransactionLite[]>([]);

    const transactionColumns = [
        {
            label: 'Date & Time',
            key: 'created_at',
            isSort: true,
            render: (val: Date) => (
                <div className={styles.date_wrapper}>
                    <span>{formatDate(val, 'MMM DD, YYYY')}</span>
                    <span>{formatDate(val, 'HH:mm')}</span>
                </div>
            )
        },
        {
            label: 'Transaction Id',
            key: 'id'
        },
        {
            label: 'Status',
            key: 'status',
            render: (val: ETransactionStatus) => (
                <Status variant={getTransactionStatus(val)}>
                    {ETransactionStatusTranslate[val][i18n.language as 'en' | 'fr']}
                </Status>
            ),
            filterOptions: [
                { label: 'Pending', value: ETransactionStatus.PENDING },
                { label: 'Confirmed', value: ETransactionStatus.APPROVED },
                { label: 'Paid', value: ETransactionStatus.ERROR },
                { label: 'Ready', value: ETransactionStatus.DECLINED },
                { label: 'Canceled', value: ETransactionStatus.CANCELED }
            ]
        },
        {
            label: 'Amount',
            key: 'amount',
            render: (val: number, item: TTransaction) => `${val} ${item.currency}`
        },
        {
            label: 'Destination',
            key: 'destination'
        }
    ];

    useEffect(() => {
        setCursor(undefined);
        setTransactions([]);
        if (date_from) {
            setCursor(date_from);
        }
        handleGetTransactions({ date_from, date_to, isNew: true });
    }, [date_to, date_from]);

    const { data: transactionsResponse, handleRequest: handleGetTransactions } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiTransactionsService.findTransactions(
                {
                    order: request?.order,
                    date_to: request?.date_to,
                    date_from: request?.date_from,
                    action: [ETransactionAction.CASHBACK]
                },
                {
                    cursor: request?.isNew ? request?.date_from : cursor,
                    limit: 10
                }
            );
        },
        onSuccess: (data) => {
            if (cursor !== data.cursor) {
                setTransactions((state) => [...state, ...data.data]);
                setCursor(data.cursor);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION
    });

    if (transactions && transactions?.length === 0) {
        return (
            <div className={styles.empty_table}>
                <img src={illustration} alt="" />
                <div className={styles.title}>No withdrawal history yet</div>
                <div className={styles.description}>
                    Once you withdraw your cashback, the history will appear here
                </div>
            </div>
        );
    }

    return (
        <Table
            //@ts-ignore
            columns={transactionColumns}
            rows={transactions}
            emptyDescription="No transactions yet"
            totalRows={transactionsResponse?.total}
            onLoadData={() => {
                handleGetTransactions();
            }}
            //@ts-ignore
            filter={{ status: filter.status }}
            //@ts-ignore
            sort={{ key: 'created_at', value: filter.order }}
            onSortChange={(_, value) => {
                setFilter((state) => ({
                    ...state,
                    order: value.toUpperCase() as 'ASC' | 'DESC'
                }));
                setCursor(undefined);
                handleGetTransactions({
                    isNew: false,
                    ...filter,
                    order: value.toUpperCase() as 'ASC' | 'DESC'
                });
            }}
            onFilterChange={(val) => {
                setFilter((state) => ({
                    ...state,
                    ...val
                }));
                setCursor(undefined);
                handleGetTransactions({
                    isNew: false,
                    ...filter,
                    ...val
                });
            }}
        />
    );
};

export default WithdrawnTable;
